import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Labs from './pages/Labs';
import LabDetail from './pages/LabDetail';
import StudentActivity from './pages/StudentActivity';
import LabInsights from './pages/LabInsights';
import StudentInsight from './pages/StudentInsight';
import Settings from './pages/Settings';
import AiUsage from './pages/AiUsage';
import Sections from './pages/Sections';
import Analytics from './pages/Analytics';
import Users from './pages/Users';
// Student pages
import StudentDashboard from './pages/StudentDashboard';
import StudentLabView from './pages/StudentLabView';
import StudentIDELaunch from './pages/StudentIDELaunch';
// A student's app address, or workspace preview, reached without its sign-in (any role)
import OpenApp from './pages/OpenApp';
import OpenPreview from './pages/OpenPreview';
// Public pages (no auth required)
import StudentOTPLogin from './pages/StudentOTPLogin';
import AccessDenied from './pages/AccessDenied';

/**
 * ProtectedRoute - Requires authentication
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg-primary)'
      }} role="status" aria-label="Loading application">
        <div className="spinner" style={{ width: 40, height: 40 }} aria-hidden="true" />
        <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/**
 * RoleBasedRoutes - Routes based on user role
 */
function RoleBasedRoutes() {
  const { isStudent, isFaculty, isAdmin, user } = useAuth();

  // Student routes
  if (isStudent) {
    return (
      <Layout userRole="student">
        <Routes>
          <Route path="/" element={<StudentDashboard />} />
          <Route path="/labs/:labId" element={<StudentLabView />} />
          <Route path="/open-app" element={<OpenApp />} />
          <Route path="/open-preview" element={<OpenPreview />} />
          {/* Redirect any faculty routes to student dashboard */}
          <Route path="/labs" element={<Navigate to="/" replace />} />
          <Route path="/settings" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    );
  }

  // Faculty/Admin routes
  if (isFaculty || isAdmin) {
    return (
      <Layout userRole={isAdmin ? 'admin' : 'faculty'}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/labs" element={<Labs />} />
          <Route path="/labs/:id" element={<LabDetail />} />
          <Route path="/labs/:labId/activity/:studentId" element={<StudentActivity />} />
          <Route path="/labs/:labId/insights" element={<LabInsights />} />
          <Route path="/labs/:labId/insights/:runId/:studentInsightId" element={<StudentInsight />} />
          <Route path="/sections" element={<Sections />} />
          <Route path="/open-app" element={<OpenApp />} />
          <Route path="/open-preview" element={<OpenPreview />} />
          <Route path="/analytics" element={<Analytics />} />
          {/* Admin-only User Management — faculty are redirected to the dashboard */}
          <Route path="/users" element={isAdmin ? <Users /> : <Navigate to="/" replace />} />
          {/* Admin-only Settings. The sidebar link is already hidden from faculty, but the
              route was reachable by typing the URL — and it now carries the environment
              catalogue, which decides what image every student in the institution runs. */}
          <Route path="/settings" element={isAdmin ? <Settings /> : <Navigate to="/" replace />} />
          {/* Admin-only: every AI request, its cost and who started it. */}
          <Route path="/ai-usage" element={isAdmin ? <AiUsage /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    );
  }

  // Unknown role - redirect to login
  console.warn('[App] Unknown user role:', user?.role);
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/student-login" element={<StudentOTPLogin />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        {/* Handles its own sign-in/loading errors; never sends a student with
            an interrupted D2L handoff to the staff login or the dashboard. */}
        <Route path="/launch/:labId" element={<StudentIDELaunch />} />
        
        {/* Protected routes with role-based rendering */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <RoleBasedRoutes />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

export default App;
